import { HelpMessage } from "@ids/react-help-message";
import classNames from "classnames";
import { type ChangeEvent, type FC, useCallback } from "react";

import { ErrorMessage } from "../errorMessage/ErrorMessage";
import { Popover } from "../popover/Popover";

type ComposedDropdownSelectProps = {
  id: string;
  className?: string;
  disabled?: boolean;
  children?: React.ReactNode;
  popoverHeader?: string | React.ReactNode;
  popoverMessage?: string | React.ReactNode;
  value: string | number | undefined;
  style?: React.CSSProperties;
  placeholder?: string;
  helpMessage?: string;
  label?: string;
  errorMessage: string | false | undefined;
  ref?: React.Ref<HTMLSelectElement>;
  onChange?: (val?: string | number | null) => void;
};

export const ComposedDropdownSelect: FC<ComposedDropdownSelectProps> = ({
  id,
  children,
  disabled,
  helpMessage,
  errorMessage,
  label,
  value = "",
  popoverHeader,
  popoverMessage,
  ref,
  className,
  style,
  placeholder,
  onChange,
}) => {
  const disabledClass = disabled ? "is-disabled" : "";
  const errorMessageId = `${id}-error-message`;
  const helpMessageId = `${id}-help-message`;
  const invalid = !!errorMessage;
  const ariaDescribedby = invalid ? errorMessageId : helpMessageId;

  const hiddenStyles: React.CSSProperties = {
    width: 0,
    height: 0,
    position: "absolute",
    visibility: "hidden",
  };

  const classes = classNames(
    "if dropdown-select",
    { "is-invalid": invalid },
    className,
  );

  const onValueChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      if (onChange) {
        onChange(e.target.value);
      }
    },
    [onChange],
  );

  return (
    <div className="if flex-column gap-8">
      <label
        className={`if label flex-row justify-content-space-between ${disabledClass}`}
        htmlFor={id}
      >
        {label}
        <Popover
          popoverHeader={popoverHeader}
          popoverMessage={popoverMessage}
        />
      </label>
      <select
        className={classes}
        id={id}
        disabled={disabled}
        aria-describedby={ariaDescribedby}
        value={value}
        ref={ref}
        style={style}
        onChange={onValueChange}
      >
        {placeholder && (
          <option disabled={true} defaultValue={""} value={""}>
            {placeholder}
          </option>
        )}
        {children}
      </select>
      {!invalid && helpMessage && (
        <HelpMessage id={helpMessageId} disabled={disabled}>
          {helpMessage}
        </HelpMessage>
      )}
      {invalid && errorMessage && (
        <ErrorMessage
          id={errorMessageId}
          style={invalid ? undefined : hiddenStyles}
        >
          {errorMessage}
        </ErrorMessage>
      )}
    </div>
  );
};
